<script setup lang="ts">
import { getTradingAppRecommendations, getTradingAppTraderAliases, getWikiStoreBanner } from '~/api/store'
import { createEmptySoftwareHomeDeviceData, getSoftwareHomeDataForDevice } from '~/api/software'
import type { TradingAppPlatform } from '~/types/homeTradingApps'
import {
  collectStoreBannerTraderCodes,
  splitStoreBannersByPosition,
  type StoreBannerItem,
} from '~/utils/homeStoreBanner'
import {
  ANDROID_PLATFORM,
  IOS_PLATFORM,
  collectTradingAppTraderCodes,
  resolveTradingAppDefaultPlatform,
} from '~/utils/homeTradingApps'

definePageMeta({
  name: 'home',
  title: 'home',
})

const { locale, t } = useAppI18n()
const appStore = useAppStore()

const adBanners = ref<StoreBannerItem[]>([])
const isPlatformResolved = ref(false)
const { data: wikiBanners, status: wikiBannersStatus } = await useAsyncData(
  `home-wiki-store-banner-${locale}`,
  async () => {
    const [iosResult, androidResult] = await Promise.allSettled([
      getWikiStoreBanner(IOS_PLATFORM),
      getWikiStoreBanner(ANDROID_PLATFORM),
    ])

    return {
      iosBanners: iosResult.status === 'fulfilled' ? iosResult.value : [],
      androidBanners: androidResult.status === 'fulfilled' ? androidResult.value : [],
    }
  },
  {
    default: () => ({
      iosBanners: [],
      androidBanners: [],
    }),
  },
)

// 按广告位拆分两端平台数据，顶部两个 Banner 分支由根节点平台 class 控制显隐。
const iosStoreBanners = computed(() => splitStoreBannersByPosition(wikiBanners.value?.iosBanners ?? []))
const androidStoreBanners = computed(() => splitStoreBannersByPosition(wikiBanners.value?.androidBanners ?? []))

/** 两端顶部 Banner 均为空时，无需等客户端识别平台即可展示默认 Hero。 */
const areBothPlatformBannersEmpty = computed(
  () => iosStoreBanners.value.topBanners.length === 0 && androidStoreBanners.value.topBanners.length === 0,
)
const showIosDefaultBackground = computed(
  () =>
    wikiBannersStatus.value === 'success' &&
    iosStoreBanners.value.topBanners.length === 0 &&
    (areBothPlatformBannersEmpty.value || isPlatformResolved.value),
)
const showAndroidDefaultBackground = computed(
  () =>
    wikiBannersStatus.value === 'success' &&
    androidStoreBanners.value.topBanners.length === 0 &&
    (areBothPlatformBannersEmpty.value || isPlatformResolved.value),
)

const { data: tradingAppsData } = await useAsyncData(
  `home-trading-apps-${locale}-${appStore.CountryCode}`,
  async () => {
    const [iosResult, androidResult] = await Promise.allSettled([
      getTradingAppRecommendations(IOS_PLATFORM),
      getTradingAppRecommendations(ANDROID_PLATFORM),
    ])

    return {
      iosRecommendations: iosResult.status === 'fulfilled' ? iosResult.value : [],
      androidRecommendations: androidResult.status === 'fulfilled' ? androidResult.value : [],
    }
  },
  {
    default: () => ({
      iosRecommendations: [],
      androidRecommendations: [],
    }),
  },
)
const { data: tradingAppTraderAliases } = await useAsyncData(
  `home-trading-apps-trader-aliases-${locale}`,
  () =>
    getTradingAppTraderAliases([
      ...collectTradingAppTraderCodes([
        ...(tradingAppsData.value?.iosRecommendations ?? []),
        ...(tradingAppsData.value?.androidRecommendations ?? []),
      ]),
      ...collectStoreBannerTraderCodes([...wikiBanners.value.iosBanners, ...wikiBanners.value.androidBanners]),
    ]),
  {
    default: () => ({}),
  },
)
const { data: softwareHomeData } = await useAsyncData(
  `store-home-software-${locale}-${appStore.CountryCode}`,
  async () => {
    const [iosResult, androidResult] = await Promise.allSettled([
      getSoftwareHomeDataForDevice(1),
      getSoftwareHomeDataForDevice(2),
    ])

    return {
      ios: iosResult.status === 'fulfilled' ? iosResult.value : createEmptySoftwareHomeDeviceData(1),
      android: androidResult.status === 'fulfilled' ? androidResult.value : createEmptySoftwareHomeDeviceData(2),
    }
  },
  {
    default: () => ({
      ios: createEmptySoftwareHomeDeviceData(1),
      android: createEmptySoftwareHomeDeviceData(2),
    }),
  },
)
const termsPageTitle = await getSeoLocaleMessage(locale, '89586')

useSeoHead({
  title: termsPageTitle,
  description: () => t('89587'),
})

const defaultTradingAppPlatform = ref<TradingAppPlatform>(IOS_PLATFORM)

onMounted(() => {
  defaultTradingAppPlatform.value = resolveTradingAppDefaultPlatform(navigator.userAgent)
  isPlatformResolved.value = true

  const platformBanners =
    defaultTradingAppPlatform.value === ANDROID_PLATFORM
      ? wikiBanners.value.androidBanners
      : wikiBanners.value.iosBanners
  const { adBanners: nextAdBanners } = splitStoreBannersByPosition(platformBanners)

  adBanners.value = nextAdBanners
})
</script>

<template>
  <main class="pt-[70px]">
    <div class="for-mac">
      <HomeWikiStoreBanner
        class="mt-[40px]"
        :banners="iosStoreBanners.topBanners"
        :trader-aliases="tradingAppTraderAliases"
        :show-default-background="showIosDefaultBackground"
      />
    </div>
    <div class="for-windows">
      <HomeWikiStoreBanner
        class="mt-[40px]"
        :banners="androidStoreBanners.topBanners"
        :trader-aliases="tradingAppTraderAliases"
        :show-default-background="showAndroidDefaultBackground"
      />
    </div>
    <div class="mx-auto flex w-[1200px] flex-col gap-[48px] pt-[40px] pb-[60px]">
      <div id="recommend" class="scroll-mt-[70px]">
        <HomeRecommendationApps
          :ios-recommendations="tradingAppsData.iosRecommendations"
          :android-recommendations="tradingAppsData.androidRecommendations"
          :default-platform="defaultTradingAppPlatform"
          :trader-aliases="tradingAppTraderAliases"
        />
      </div>
      <HomeStoreAdBanner v-if="adBanners.length" :banners="adBanners" :trader-aliases="tradingAppTraderAliases" />
      <HomeLocalPopularApps :software-home-data="softwareHomeData" :device-type="defaultTradingAppPlatform" />
      <HomeMonthlyRanking :software-home-data="softwareHomeData" :device-type="defaultTradingAppPlatform" />
      <HomeCuratedCryptoTools :software-home-data="softwareHomeData" :device-type="defaultTradingAppPlatform" />
    </div>
  </main>
</template>

<style scoped lang="less">
/* 两个平台都保留 SSR HTML，由首屏前注入的根节点 class 选择当前可见分支。 */
:global(html.Android .for-mac) {
  display: none !important;
}

:global(html.IOS .for-windows) {
  display: none !important;
}
</style>
